export function NumberCounter() {
    document.addEventListener('DOMContentLoaded', () => {
        const targets = document.querySelectorAll('[data-number-counter]');
        if (!targets.length) return;

        const DURATION = 1500;
        const ENTER_DELAY = 400;
        const EXIT_DELAY = 400;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const activeFrames = new WeakMap();
        const pendingTimeouts = new WeakMap();

        // Rozkłada np. "+4 lekcji" / "-10" / "10,5 min" / "$100" na części,
        // żeby animować tylko liczbę, a prefix/suffix/forsowany znak `+` zachować.
        const parseTarget = (element) => {
            const raw = (element.dataset.numberCounter || '').trim();
            const match = raw.match(/^(.*?)([+-]?\d+(?:[.,]\d+)?)(.*)$/s);
            if (!match) return null;

            const [, prefix, numberPart, suffix] = match;
            const explicitPlus = numberPart.startsWith('+');
            const value = parseFloat(numberPart.replace(/^\+/, '').replace(',', '.'));
            if (Number.isNaN(value)) return null;

            const decimals = (Math.abs(value).toString().split('.')[1] || '').length;
            return { prefix, suffix, value, decimals, explicitPlus };
        };

        const formatValue = (current, target) => {
            const fixed = target.decimals > 0
                ? current.toFixed(target.decimals).replace('.', ',')
                : Math.round(current).toString();
            const signed = target.explicitPlus && current >= 0 ? `+${fixed}` : fixed;
            return `${target.prefix}${signed}${target.suffix}`;
        };

        const cancelActive = (element) => {
            const id = activeFrames.get(element);
            if (id != null) {
                cancelAnimationFrame(id);
                activeFrames.delete(element);
            }
        };

        const animate = (element, target) => {
            cancelActive(element);

            if (prefersReducedMotion) {
                element.textContent = formatValue(target.value, target);
                return;
            }

            const start = performance.now();
            const easeOut = (t) => 1 - Math.pow(1 - t, 3);

            const tick = (now) => {
                const progress = Math.min((now - start) / DURATION, 1);
                element.textContent = formatValue(target.value * easeOut(progress), target);
                if (progress < 1) {
                    activeFrames.set(element, requestAnimationFrame(tick));
                } else {
                    activeFrames.delete(element);
                }
            };

            activeFrames.set(element, requestAnimationFrame(tick));
        };

        const reset = (element, target) => {
            cancelActive(element);
            element.textContent = formatValue(0, target);
        };

        // Po wyjściu z viewportu zerujemy do 0 → przy ponownym wejściu animacja
        // odpala się od zera. Bez tego, drugie i kolejne wejścia widziałyby tylko
        // final value, a user dostawałby efekt "animuje się tylko za pierwszym razem".
        //
        // Obie akcje (animate przy wejściu, reset przy wyjściu) opóźnione o ENTER_DELAY/
        // EXIT_DELAY ms, żeby zmiany stanu nie były instantowe — wpadające w viewport
        // itemy najpierw się "osadzą" zanim cyferki wystartują. Szybka zmiana kierunku
        // anuluje pending timeout, więc nie ma flickeringu przy "brzegowych" pozycjach.
        // Histereza progów: animacja odpala dopiero gdy kafelek jest WYRAŹNIE widoczny
        // (≥ENTER_RATIO), a reset do 0 dopiero gdy CAŁKIEM zniknie (nie intersectuje).
        // Pomiędzy — martwa strefa, w której stan się nie zmienia. Bez tego symetryczny
        // próg 0.8 resetował wartość gdy tylko widoczność spadła <80% (np. częściowo
        // przycięty/scroll-driven kafelek w sticky-section) → cyferki migotały i skakały.
        const ENTER_RATIO = 0.8;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const target = entry.target._numberCounterTarget;
                if (!target) return;

                let action;
                if (entry.intersectionRatio >= ENTER_RATIO) action = animate;
                else if (!entry.isIntersecting) action = reset;
                else return; // martwa strefa — bez zmiany stanu

                const pending = pendingTimeouts.get(entry.target);
                if (pending != null) {
                    clearTimeout(pending);
                    pendingTimeouts.delete(entry.target);
                }

                const delay = action === animate ? ENTER_DELAY : EXIT_DELAY;

                const timeoutId = setTimeout(() => {
                    action(entry.target, target);
                    pendingTimeouts.delete(entry.target);
                }, delay);

                pendingTimeouts.set(entry.target, timeoutId);
            });
        }, { threshold: [0, ENTER_RATIO] });

        targets.forEach((element) => {
            const target = parseTarget(element);
            if (!target) return;
            element._numberCounterTarget = target;
            element.textContent = formatValue(0, target);
            observer.observe(element);
        });
    });
}
